import React, { useState, useCallback, useEffect } from 'react';
import { DatePicker, Button, Select, Card, message, Tabs, Table, Badge, Space, Spin, Divider, Checkbox, Col, Row, Form } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_TXALERT, API_MERCHANT, API_RETAILER, API_SITE } from '../../config/config';
import htmlParser from 'html-react-parser';
import GroupAccess from './../../helper/GroupAccess';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const MERCHANT_DATA_URL = API_MERCHANT + '/list';
const RETAILER_DATA_URL = API_RETAILER + '/list';
const SITE_DATA_URL = API_SITE + '/list';

const CliqReport = () => {
  const [dateRange, setDateRange] = useState([
    moment().startOf('month'),
    moment().endOf('month')
  ]);
  const [filters, setFilters] = useState({
    provider: [],
    merchant: [],
    retailer: [],
    site: []
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [merchantList, setMerchantList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [inactiveChecked, setInactiveChecked] = useState(false);

  // Unified pagination state for all tabs
  const [tabsPagination, setTabsPagination] = useState({
    tab1: { current: 1, pageSize: 20 },
    tab2: { current: 1, pageSize: 20 },
    tab3: { current: 1, pageSize: 20 },
    tab4: { current: 1, pageSize: 20 },
    tab5: { current: 1, pageSize: 20 }
  });

  // Fetch merchant list
  const getMerchantList = useCallback(() => {
    axios
      .post(
        MERCHANT_DATA_URL,
        { getall: true },
        { headers: { Authorization: 'Bearer ' + Cookies.get('access_token'), Authuser: Cookies.get('authuser') } }
      )
      .then(async ({ data }) => {
        await setMerchantList(data.list);
      })
      .catch(() => false);
  }, []);

  // Fetch retailer list
  const getRetailerList = useCallback((mid_change) => {
    axios
      .post(
        RETAILER_DATA_URL,
        { mid: mid_change },
        { headers: { Authorization: 'Bearer ' + Cookies.get('access_token'), Authuser: Cookies.get('authuser') } }
      )
      .then(async ({ data }) => {
        await setRetailerList(data.list);
      })
      .catch(() => false);
  }, []);

  // Fetch site list
  const getSiteList = useCallback((mid_change, rid_change) => {
    axios
      .post(
        SITE_DATA_URL,
        { mid: mid_change, rid: rid_change },
        { headers: { Authorization: 'Bearer ' + Cookies.get('access_token'), Authuser: Cookies.get('authuser') } }
      )
      .then(async ({ data }) => {
        await setSiteList(data.list);
      })
      .catch(() => false);
  }, []);

  // Handle merchant change
  const onChangeMid = (mid_change) => {
    setFilters({ ...filters, merchant: mid_change, retailer: [], site: [] });
    getRetailerList(mid_change);
    getSiteList(mid_change, null);
  };

  // Handle retailer change
  const onChangeRid = (rid_change) => {
    setFilters({ ...filters, retailer: rid_change, site: [] });
    getSiteList(filters.merchant, rid_change);
  };

  // Handle inactive checkbox change
  const onChangeInactive = (e) => {
    setInactiveChecked(e.target.checked);
  };

  // Generic pagination handler for all tabs
  const handlePaginationChange = (tabKey) => (page, pageSize) => {
    setTabsPagination(prev => ({
      ...prev,
      [tabKey]: { current: page, pageSize }
    }));
  };

  // Get merchant options
  const getMerchant = () => {
    if (inactiveChecked) {
      return merchantList.map((item) =>
        item.status === 'active' ? (
          <Select.Option key={item.mid} value={item.mid}>
            {htmlParser(item.name) + ` (mid: ` + item.mid + `)`}
          </Select.Option>
        ) : (
          <Select.Option key={item.mid} value={item.mid} style={{ color: '#f5222d' }}>
            {htmlParser(item.name) + ` (mid: ` + item.mid + `)`}
          </Select.Option>
        )
      );
    } else {
      return merchantList.map((item) =>
        item.status === 'active' ? (
          <Select.Option key={item.mid} value={item.mid}>
            {htmlParser(item.name) + ` (mid: ` + item.mid + `)`}
          </Select.Option>
        ) : null
      );
    }
  };

  // Load lists on mount
  useEffect(() => {
    if (merchantList.length === 0) {
      getMerchantList();
    }
    if (retailerList.length === 0) {
      getRetailerList();
    }
    if (siteList.length === 0) {
      getSiteList();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select when only one merchant is available
  useEffect(() => {
    if (merchantList.length === 1 && filters.merchant.length === 0) {
      const singleMid = merchantList[0].mid;
      setFilters(prev => ({ ...prev, merchant: [singleMid] }));
      getRetailerList([singleMid]);
      getSiteList([singleMid], null);
    }
  }, [merchantList, filters.merchant.length, getRetailerList, getSiteList]);

  // Auto-select when only one retailer is available
  useEffect(() => {
    if (
      retailerList.length === 1 &&
      filters.retailer.length === 0 &&
      filters.merchant.length > 0
    ) {
      const singleRid = retailerList[0].rid;
      setFilters(prev => {
        // Use the current merchant value from state
        getSiteList(prev.merchant, [singleRid]);
        return { ...prev, retailer: [singleRid] };
      });
    }
  }, [retailerList, filters.retailer.length, filters.merchant.length, getSiteList]);

  // Auto-select when only one site is available
  useEffect(() => {
    const hasParentSelection = filters.merchant.length > 0 || filters.retailer.length > 0;
    if (siteList.length === 1 && filters.site.length === 0 && hasParentSelection) {
      const singleSid = siteList[0].sid;
      setFilters(prev => ({ ...prev, site: [singleSid] }));
    }
  }, [siteList, filters.site.length, filters.merchant.length, filters.retailer.length]);

  // Generate preview data
  const handleGeneratePreview = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a valid date range');
      return;
    }

    // Validate date range (max 1 month)
    const monthsDiff = dateRange[1].diff(dateRange[0], 'months', true);
    if (monthsDiff > 1) {
      message.error('Date range cannot exceed 1 month');
      return;
    }

    setLoading(true);
    setReportData(null);

    // Reset pagination for all tabs
    setTabsPagination({
      tab1: { current: 1, pageSize: 20 },
      tab2: { current: 1, pageSize: 20 },
      tab3: { current: 1, pageSize: 20 },
      tab4: { current: 1, pageSize: 20 },
      tab5: { current: 1, pageSize: 20 }
    });

    try {
      const payload = {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
        report_type: "cliq_operations_report",
        preview: true,
        page: 1,
        size: 999999
      };

      // Add filters only if they have values
      if (filters.provider.length > 0) payload.provider = filters.provider;
      if (filters.merchant.length > 0) payload.mid = filters.merchant;
      if (filters.retailer.length > 0) payload.rid = filters.retailer;
      if (filters.site.length > 0) payload.sid = filters.site;

      const response = await axios.post(
        API_TXALERT + "/reports",
        payload,
        {
          headers: {
            Authorization: "Bearer " + Cookies.get("access_token"),
            Authuser: Cookies.get("authuser")
          }
        }
      );

      if (response.data && response.data.data) {
        setReportData(response.data.data);
        const totalAlerts = response.data.count || 0;
        message.success(`Report preview loaded: ${totalAlerts} alerts`);
      } else {
        message.info('No alerts found for the selected date range and filters.');
      }
    } catch (error) {
      console.error('Preview generation error:', error);
      if (error.response && error.response.status === 408) {
        message.error('Report generation is taking longer than expected. Please try with a smaller date range.');
      } else {
        message.error('Failed to generate preview: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Download XLSX file
  const handleDownloadXLSX = async () => {
    setDownloading(true);

    try {
      const payload = {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
        report_type: "cliq_operations_report",
        preview: false,
        export_filetype: "xlsx",
        page: 1,
        size: 999999
      };

      // Add filters only if they have values
      if (filters.provider.length > 0) payload.provider = filters.provider;
      if (filters.merchant.length > 0) payload.mid = filters.merchant;
      if (filters.retailer.length > 0) payload.rid = filters.retailer;
      if (filters.site.length > 0) payload.sid = filters.site;

      const response = await axios.post(
        API_TXALERT + "/reports",
        payload,
        {
          headers: {
            Authorization: "Bearer " + Cookies.get("access_token"),
            Authuser: Cookies.get("authuser")
          }
        }
      );

      if (response.data && response.data.data) {
        // Trigger download
        const downloadUrl = response.data.data;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `cliq_operations_report_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.success('Report downloaded successfully');
      }
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download report: ' + (error.message || 'Unknown error'));
    } finally {
      setDownloading(false);
    }
  };

  // Table columns for Tab 1: Alert Summary
  const tab1Columns = [
    {
      title: 'Date Range',
      dataIndex: 'date_range',
      key: 'date_range',
      width: 180
    },
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      width: 100
    },
    {
      title: 'MID(s)',
      dataIndex: 'mids',
      key: 'mids',
      width: 200
    },
    {
      title: 'Merchant Name',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      width: 200
    },
    {
      title: 'Merchant Group',
      dataIndex: 'merchant_group',
      key: 'merchant_group',
      width: 200
    },
    {
      title: 'Ethoca Alerts',
      dataIndex: 'ethoca_alerts',
      key: 'ethoca_alerts',
      width: 120,
      align: 'right'
    },
    {
      title: 'CDRN Alerts',
      dataIndex: 'cdrn_alerts',
      key: 'cdrn_alerts',
      width: 120,
      align: 'right'
    },
    {
      title: 'RDR Alerts',
      dataIndex: 'rdr_alerts',
      key: 'rdr_alerts',
      width: 120,
      align: 'right'
    },
    {
      title: 'Total',
      dataIndex: 'alert_count_total',
      key: 'alert_count_total',
      width: 100,
      align: 'right',
      render: (text) => <strong>{text}</strong>
    }
  ];

  // Table columns for Tab 2: Alert by MID
  const tab2Columns = [
    {
      title: 'Date Range',
      dataIndex: 'date_range',
      key: 'date_range',
      width: 180
    },
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      width: 100
    },
    {
      title: 'MID',
      dataIndex: 'mid',
      key: 'mid',
      width: 150
    },
    {
      title: 'Merchant Name',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      width: 200
    },
    {
      title: 'Merchant Group',
      dataIndex: 'merchant_group',
      key: 'merchant_group',
      width: 200
    },
    {
      title: 'Ethoca Alerts',
      dataIndex: 'ethoca_alerts',
      key: 'ethoca_alerts',
      width: 120,
      align: 'right'
    },
    {
      title: 'CDRN Alerts',
      dataIndex: 'cdrn_alerts',
      key: 'cdrn_alerts',
      width: 120,
      align: 'right'
    },
    {
      title: 'RDR Alerts',
      dataIndex: 'rdr_alerts',
      key: 'rdr_alerts',
      width: 120,
      align: 'right'
    },
    {
      title: 'Total',
      dataIndex: 'alert_count_total',
      key: 'alert_count_total',
      width: 100,
      align: 'right',
      render: (text) => <strong>{text}</strong>
    }
  ];

  // Table columns for Tab 3: Alert with MID
  const tab3Columns = [
    { title: 'Alert Date', dataIndex: 'alert_date', key: 'alert_date', width: 120 },
    { title: 'Alert Status', dataIndex: 'alert_status', key: 'alert_status', width: 120 },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 100, align: 'right' },
    { title: 'ARN', dataIndex: 'arn', key: 'arn', width: 200 },
    { title: 'Auth Code', dataIndex: 'auth_code', key: 'auth_code', width: 100 },
    { title: 'Card Number', dataIndex: 'card_number', key: 'card_number', width: 150 },
    { title: 'Condition Code', dataIndex: 'condition_code', key: 'condition_code', width: 120 },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: 'Merchant Name', dataIndex: 'merchant_name', key: 'merchant_name', width: 200 },
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Issuer Name', dataIndex: 'issuername', key: 'issuername', width: 150 },
    { title: 'Dispute Amount', dataIndex: 'dispute_amount', key: 'dispute_amount', width: 120, align: 'right' },
    { title: 'Dispute Currency', dataIndex: 'dispute_currency', key: 'dispute_currency', width: 120 },
    { title: 'Matched Descriptor', dataIndex: 'matched_descriptor', key: 'matched_descriptor', width: 200 },
    { title: 'MCC', dataIndex: 'mcc', key: 'mcc', width: 80 },
    { title: 'Merchant Update On', dataIndex: 'merchant_update_on', key: 'merchant_update_on', width: 140 },
    { title: 'Outcome', dataIndex: 'outcome', key: 'outcome', width: 120 },
    { title: 'Outcome Update On', dataIndex: 'outcome_update_on', key: 'outcome_update_on', width: 140 },
    { title: 'Provider', dataIndex: 'provider', key: 'provider', width: 150 },
    { title: 'Provider Reference', dataIndex: 'provider_reference', key: 'provider_reference', width: 150 },
    { title: 'Reason Code', dataIndex: 'reason_code', key: 'reason_code', width: 120 },
    { title: 'Retailer Name', dataIndex: 'retailer_name', key: 'retailer_name', width: 200 },
    { title: 'Site Name', dataIndex: 'site_name', key: 'site_name', width: 200 },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 100 },
    { title: 'Tier', dataIndex: 'tier', key: 'tier', width: 80 },
    { title: 'Transaction Date', dataIndex: 'transaction_date', key: 'transaction_date', width: 140 },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 120 },
    { title: 'Updated By', dataIndex: 'updated_by', key: 'updated_by', width: 120 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 300 },
    { title: 'Merchant Transaction ID', dataIndex: 'merchant_transaction_id', key: 'merchant_transaction_id', width: 200 },
    { title: 'MID', dataIndex: 'mid', key: 'mid', width: 120 }
  ];

  // Table columns for Tab 4: Raw Data
  const tab4Columns = [
    { title: 'Alert Date', dataIndex: 'alert_date', key: 'alert_date', width: 180 },
    { title: 'Alert Reason', dataIndex: 'alert_reason', key: 'alert_reason', width: 150 },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 100, align: 'right' },
    { title: 'ARN', dataIndex: 'arn', key: 'arn', width: 200 },
    { title: 'Auth Code', dataIndex: 'auth_code', key: 'auth_code', width: 100 },
    { title: 'Card No.', dataIndex: 'card_number', key: 'card_number', width: 150 },
    { title: 'Condition Code', dataIndex: 'condition_code', key: 'condition_code', width: 120 },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: 'Descriptor', dataIndex: 'descriptor', key: 'descriptor', width: 200 },
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Issuer Name', dataIndex: 'issuername', key: 'issuername', width: 150 },
    { title: 'Dispute Amount', dataIndex: 'dispute_amount', key: 'dispute_amount', width: 120, align: 'right' },
    { title: 'Dispute Currency', dataIndex: 'dispute_currency', key: 'dispute_currency', width: 120 },
    { title: 'Matched Descriptor', dataIndex: 'matched_descriptor', key: 'matched_descriptor', width: 200 },
    { title: 'MCC', dataIndex: 'mcc', key: 'mcc', width: 80 },
    { title: 'Merchant Update On', dataIndex: 'merchant_update_on', key: 'merchant_update_on', width: 180 },
    { title: 'Outcome', dataIndex: 'outcome', key: 'outcome', width: 120 },
    { title: 'Outcome Update On', dataIndex: 'outcome_update_on', key: 'outcome_update_on', width: 180 },
    { title: 'Provider', dataIndex: 'provider', key: 'provider', width: 150 },
    { title: 'Provider Reference', dataIndex: 'provider_reference', key: 'provider_reference', width: 150 },
    { title: 'Reason Code', dataIndex: 'reason_code', key: 'reason_code', width: 120 },
    { title: 'Retailer Name', dataIndex: 'retailer_name', key: 'retailer_name', width: 200 },
    { title: 'Site Name', dataIndex: 'site_name', key: 'site_name', width: 200 },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 100 },
    { title: 'Tier', dataIndex: 'tier', key: 'tier', width: 80 },
    { title: 'Transaction Date', dataIndex: 'transaction_date', key: 'transaction_date', width: 180 },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 120 },
    { title: 'Updated By', dataIndex: 'updated_by', key: 'updated_by', width: 120 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 300 },
    { title: 'Merchant Transaction ID', dataIndex: 'merchant_transaction_id', key: 'merchant_transaction_id', width: 200 }
  ];

  // Table columns for Tab 5: Report Issues
  const tab5Columns = [
    {
      title: 'Alert ID',
      dataIndex: 'alert_id',
      key: 'alert_id',
      width: 100
    },
    {
      title: 'Issue Type',
      dataIndex: 'issue_type',
      key: 'issue_type',
      width: 180
    },
    {
      title: 'Issue Description',
      dataIndex: 'issue_description',
      key: 'issue_description',
      width: 300
    },
    {
      title: 'Alert Date',
      dataIndex: 'alert_date',
      key: 'alert_date',
      width: 120
    },
    {
      title: 'Merchant Name',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      width: 200
    },
    {
      title: 'Notes Excerpt',
      dataIndex: 'notes_excerpt',
      key: 'notes_excerpt',
      width: 250
    },
    {
      title: 'Recommendation',
      dataIndex: 'recommendation',
      key: 'recommendation',
      width: 250
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Cliq Operation Report" style={{ marginBottom: 16 }}>
        <Form size="large">
          <Row gutter={[16, 16]}>
            {/* Date Range Picker */}
            <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
              <Form.Item>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Date Range <span style={{ color: 'red' }}>*</span>
                </label>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  placeholder={['Start Date', 'End Date']}
                />
                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                  Maximum 1 month
                </div>
              </Form.Item>
            </Col>

            {/* Merchant Filter */}
            <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
              <Form.Item>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Merchant (Optional)
                </label>
                <Select
                  showSearch
                  mode="multiple"
                  showArrow={true}
                  maxTagCount="responsive"
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  onChange={onChangeMid}
                  value={filters.merchant}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  filterSort={(optionA, optionB) => optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())}
                  placeholder="Select merchants"
                  allowClear
                  dropdownRender={(menu) =>
                    GroupAccess('SYSADMIN') ? (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ padding: '0 8px 4px' }}>
                          <Checkbox onChange={onChangeInactive}>Show Inactive?</Checkbox>
                        </Space>
                      </>
                    ) : (
                      <>{menu}</>
                    )
                  }
                >
                  {merchantList ? getMerchant() : null}
                </Select>
              </Form.Item>
            </Col>

            {/* Retailer Filter */}
            <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
              <Form.Item>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Retailer (Optional)
                </label>
                <Select
                  showSearch
                  mode="multiple"
                  showArrow={true}
                  maxTagCount="responsive"
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  onChange={onChangeRid}
                  value={filters.retailer}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  filterSort={(optionA, optionB) => optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())}
                  placeholder="Select retailers"
                  allowClear
                >
                  {retailerList
                    ? retailerList.map((item) => (
                        <Select.Option key={item.rid} value={item.rid}>
                          {htmlParser(item.name) + ` (rid: ` + item.rid + `)`}
                        </Select.Option>
                      ))
                    : null}
                </Select>
              </Form.Item>
            </Col>

            {/* Site Filter */}
            <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
              <Form.Item>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Site (Optional)
                </label>
                <Select
                  showSearch
                  mode="multiple"
                  showArrow={true}
                  maxTagCount="responsive"
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  value={filters.site}
                  onChange={(value) => setFilters({ ...filters, site: value })}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  filterSort={(optionA, optionB) => optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())}
                  placeholder="Select sites"
                  allowClear
                >
                  {siteList
                    ? siteList.map((item) => (
                        <Select.Option key={item.sid} value={item.sid}>
                          {htmlParser(item.name) + ` (sid: ` + item.sid + `)`}
                        </Select.Option>
                      ))
                    : null}
                </Select>
              </Form.Item>
            </Col>

            {/* Action Buttons */}
            {reportData && (
              <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6, offset: 12 }} lg={{ span: 6, offset: 12 }} xl={{ span: 4, offset: 13 }}>
                <Form.Item>
                  <Button
                    type="default"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadXLSX}
                    loading={downloading}
                    block
                  >
                    Download XLSX
                  </Button>
                </Form.Item>
              </Col>
            )}

            <Col
              className="gutter-row"
              xs={{ span: 24 }}
              sm={{ span: 12 }}
              md={{ span: 6, offset: reportData ? 0 : 12 }}
              lg={{ span: 6, offset: reportData ? 0 : 12}}
              xl={{ span: 4, offset: reportData ? 0 : 17 }}
            >
              <Form.Item>
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={handleGeneratePreview}
                  loading={loading}
                >
                  Generate Report Preview
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: 16 }}>
              Loading report data...
            </div>
          </div>
        </Card>
      )}

      {/* Report Preview Tabs */}
      {reportData && !loading && (
        <Card>
          <Tabs defaultActiveKey="1" type="card">
            {/* Tab 1: Alert Summary */}
            <TabPane
              tab={
                <span>
                  Alert Summary{' '}
                  {reportData.metadata && reportData.metadata.tab_counts && (
                    <Badge
                      count={reportData.metadata.tab_counts.tab1 || 0}
                      style={{ backgroundColor: '#52c41a' }}
                    />
                  )}
                </span>
              }
              key="1"
            >
              <Table
                columns={tab1Columns}
                dataSource={reportData.tab1_summary || []}
                pagination={{
                  current: tabsPagination.tab1.current,
                  pageSize: tabsPagination.tab1.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `Total ${total} rows`,
                  onChange: handlePaginationChange('tab1'),
                  onShowSizeChange: handlePaginationChange('tab1')
                }}
                scroll={{ x: 'max-content' }}
                size="small"
                bordered
                rowKey={(record) => `tab1-${record.date_range}-${record.period}-${record.mids}-${record.merchant_name}-${record.merchant_group}`}
              />
            </TabPane>

            {/* Tab 2: Alert by MID */}
            <TabPane
              tab={
                <span>
                  Alert by MID{' '}
                  {reportData.metadata && reportData.metadata.tab_counts && (
                    <Badge
                      count={reportData.metadata.tab_counts.tab2 || 0}
                      style={{ backgroundColor: '#1890ff' }}
                    />
                  )}
                </span>
              }
              key="2"
            >
              <Table
                columns={tab2Columns}
                dataSource={reportData.tab2_by_mid || []}
                pagination={{
                  current: tabsPagination.tab2.current,
                  pageSize: tabsPagination.tab2.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `Total ${total} rows`,
                  onChange: handlePaginationChange('tab2'),
                  onShowSizeChange: handlePaginationChange('tab2')
                }}
                scroll={{ x: 'max-content' }}
                size="small"
                bordered
                rowKey={(record) => `tab2-${record.mid}-${record.date_range}-${record.period}-${record.merchant_name}`}
              />
            </TabPane>

            {/* Tab 3: Alert with MID */}
            <TabPane
              tab={
                <span>
                  Alert with MID{' '}
                  {reportData.metadata && reportData.metadata.tab_counts && (
                    <Badge
                      count={reportData.metadata.tab_counts.tab3 || 0}
                      style={{ backgroundColor: '#722ed1' }}
                    />
                  )}
                </span>
              }
              key="3"
            >
              <Table
                columns={tab3Columns}
                dataSource={reportData.tab3_detail || []}
                pagination={{
                  current: tabsPagination.tab3.current,
                  pageSize: tabsPagination.tab3.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `Total ${total} rows`,
                  onChange: handlePaginationChange('tab3'),
                  onShowSizeChange: handlePaginationChange('tab3')
                }}
                scroll={{ x: 'max-content' }}
                size="small"
                bordered
                rowKey={(record) => `tab3-${record.id}`}
              />
            </TabPane>

            {/* Tab 4: Raw Data */}
            <TabPane
              tab={
                <span>
                  Raw Data{' '}
                  {reportData.metadata && reportData.metadata.tab_counts && (
                    <Badge
                      count={reportData.metadata.tab_counts.tab4 || 0}
                      style={{ backgroundColor: '#fa8c16' }}
                    />
                  )}
                </span>
              }
              key="4"
            >
              <Table
                columns={tab4Columns}
                dataSource={reportData.tab4_raw || []}
                pagination={{
                  current: tabsPagination.tab4.current,
                  pageSize: tabsPagination.tab4.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `Total ${total} rows`,
                  onChange: handlePaginationChange('tab4'),
                  onShowSizeChange: handlePaginationChange('tab4')
                }}
                scroll={{ x: 'max-content' }}
                size="small"
                bordered
                rowKey={(record) => `tab4-${record.id}`}
              />
            </TabPane>

            {/* Tab 5: Report Issues (Conditional) */}
            {reportData.tab5_issues && reportData.tab5_issues.length > 0 && (
              <TabPane
                tab={
                  <span>
                    Report Issues{' '}
                    {reportData.metadata && reportData.metadata.tab_counts && (
                      <Badge
                        count={reportData.metadata.tab_counts.tab5 || 0}
                        status="warning"
                      />
                    )}
                  </span>
                }
                key="5"
              >
                <Table
                  columns={tab5Columns}
                  dataSource={reportData.tab5_issues || []}
                  pagination={{
                    current: tabsPagination.tab5.current,
                    pageSize: tabsPagination.tab5.pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Total ${total} issues`,
                    onChange: handlePaginationChange('tab5'),
                    onShowSizeChange: handlePaginationChange('tab5')
                  }}
                  scroll={{ x: 'max-content' }}
                  size="small"
                  bordered
                  rowKey={(record) => `tab5-${record.alert_id}`}
                />
              </TabPane>
            )}
          </Tabs>
        </Card>
      )}
    </div>
  );
};

export default CliqReport;
